import TrendingDownIcons from '@/icons/TrendingDownIcons'
import TrendingUpIcons from '@/icons/TrendingUpIcons'
import { formatNumberUsage, formatNumeral } from '@/utils/function'
import { TFunction } from 'i18next'
import React from 'react'
import { Typogaphy } from 'vnetwork-ui'
import { GridColDefVNW } from 'vnetwork-ui/dist/components/DataGridPro'
import {
    BuiltAnalyticsData,
    DataBuilderConfig,
    getAllUsageKeys,
    getAllHierarchicalKeys,
    getColumnHeaders,
} from '../utils/functions'
import { DisplayConfig } from './index'
import CellLink from '@/components/GokuDataGrid/CellLink'
import { generatePathDatacentral, paths } from '@/utils/path'

// Growth indicator component
const GrowthIndicator: React.FC<{
    currentValue: number
    previousValue: number
    unit: string
}> = ({ currentValue, previousValue, unit }) => {
    if (previousValue === 0 && currentValue === 0) return null

    const isGrowth = currentValue > previousValue
    const isUnchanged = currentValue === previousValue

    if (isUnchanged) return null

    // Calculate percentage change
    const percentChange =
        previousValue === 0
            ? currentValue > 0
                ? 100
                : 0
            : ((currentValue - previousValue) / previousValue) * 100

    // Calculate absolute change
    const absoluteChange = currentValue - previousValue
    const formattedChange = formatNumberUsage(Math.abs(absoluteChange))

    return (
        <div
            className={`text-xs flex items-center gap-1 ${
                isUnchanged
                    ? 'text-gray-400'
                    : isGrowth
                      ? 'text-green-600'
                      : 'text-red-600'
            }`}
        >
            <span className="text-sm">
                {isUnchanged ? (
                    ''
                ) : isGrowth ? (
                    <TrendingUpIcons className="w-4 h-4" />
                ) : (
                    <TrendingDownIcons className="w-4 h-4" />
                )}
            </span>
            <span
                title={`${isGrowth ? '+' : '-'}${formattedChange} ${unit} (${percentChange.toFixed(1)}%)`}
            >
                {percentChange.toFixed(1)}%
            </span>
        </div>
    )
}

// Cell component for usage values
const CellUsage: React.FC<{
    value: number
    unit: string
    count: number
    aggregationMethod: string
    originalValue?: number
    originalUnit?: string
    previousValue?: number
    showGrowth?: boolean
    displayConfig: DisplayConfig
}> = ({
    value,
    unit,
    count,
    aggregationMethod,
    originalValue,
    originalUnit,
    previousValue,
    showGrowth = true,
    displayConfig,
}) => {
    if (count === 0) return <span className="text-gray-400">-</span>

    const formattedValue = formatNumeral(value, 0)

    // Create tooltip text with conversion info if available
    let titleText = `${aggregationMethod}: ${formattedValue} ${unit} (${count} records)`
    if (
        originalValue !== undefined &&
        originalUnit &&
        originalValue !== value
    ) {
        const formattedOriginal = formatNumeral(originalValue)
        titleText += `\nOriginal: ${formattedOriginal} ${originalUnit}`
        titleText += `\nConversion factor: ${value / originalValue}`
    }

    // Render based on display mode
    switch (displayConfig.mode) {
        case 'default':
            return (
                <div
                    className="text-right flex w-full justify-end items-center gap-1"
                    title={titleText}
                >
                    {displayConfig.showGrowthIndicators &&
                        showGrowth &&
                        previousValue !== undefined && (
                            <GrowthIndicator
                                currentValue={value}
                                previousValue={previousValue}
                                unit={unit}
                            />
                        )}
                    <Typogaphy>{formattedValue}</Typogaphy>
                </div>
            )

        case 'detailed':
            return (
                <div
                    className="text-right flex w-full justify-end items-center gap-1"
                    title={titleText}
                >
                    {displayConfig.showGrowthIndicators &&
                        showGrowth &&
                        previousValue !== undefined && (
                            <GrowthIndicator
                                currentValue={value}
                                previousValue={previousValue}
                                unit={unit}
                            />
                        )}
                    <div
                        className=""
                        title={`Original: ${formatNumberUsage(originalValue || 0)} ${originalUnit}`}
                    >
                        {formatNumeral(originalValue || 0, 0)}
                    </div>
                </div>
            )

        case 'two-line':
            return (
                <div className="text-right" title={titleText}>
                    <div className="flex gap-1 items-center justify-end">
                        {displayConfig.showGrowthIndicators &&
                            showGrowth &&
                            previousValue !== undefined && (
                                <GrowthIndicator
                                    currentValue={value}
                                    previousValue={previousValue}
                                    unit={unit}
                                />
                            )}
                        <Typogaphy className="font-medium">
                            {formattedValue}
                        </Typogaphy>
                    </div>
                    {displayConfig?.showOriginalValues &&
                        originalValue !== undefined &&
                        originalValue !== value && (
                            <div
                                className="text-xs text-blue-500"
                                title={`Original: ${formatNumberUsage(originalValue)} ${originalUnit}`}
                            >
                                ≈ {formatNumeral(originalValue)} {originalUnit}
                            </div>
                        )}
                </div>
            )

        default:
            return (
                <div className="text-right" title={titleText}>
                    <Typogaphy>{formattedValue}</Typogaphy>
                </div>
            )
    }
}

// Generate dynamic columns based on built data
export const generateAnalyticsColumns = (
    data: BuiltAnalyticsData[],
    config: DataBuilderConfig,
    displayConfig: DisplayConfig,
    t: TFunction,
    productGroupName?: string
): GridColDefVNW<BuiltAnalyticsData>[] => {
    const columns: GridColDefVNW<BuiltAnalyticsData>[] = []

    // Static columns
    columns.push(
        {
            field: 'customer_name',
            headerName: 'Tên Công Ty/ Cá Nhân',
            width: 500,
            renderCell: ({ row }) => {
                return (
                    <CellLink
                        to={generatePathDatacentral(paths.customerDetail, {
                            id: row.customer_id,
                        })}
                    >
                        {row?.customer_id} - {row?.customer_name}
                    </CellLink>
                )
            },
        },
        {
            field: 'service_entity_name',
            headerName: 'Tên Portal',
            cellClassName: 'uppercase',
            width: 240,
        }
    )

    // Get all periods and usage keys
    const periods = getColumnHeaders(data, config)
    const isVNIS = productGroupName === 'VNIS'
    const keys = isVNIS ? getAllHierarchicalKeys(data) : getAllUsageKeys(data)

    // Create column groups for each period
    periods.forEach((periodKey, periodIndex) => {
        // Create sub-columns for each key in this period
        keys?.forEach((key) => {
            const columnKey = `${periodKey}_${key}`
            const previousPeriodKey = periods[periodIndex - 1] // Get previous period
            const isFirstPeriod = periodIndex === 0

            // Adjust column width based on display mode
            const columnWidth =
                displayConfig.mode === 'detailed'
                    ? 200
                    : displayConfig.mode === 'two-line'
                      ? 180
                      : 160

            // For VNIS hierarchical keys, extract provider name for display
            let displayName = key
            let providerName = ''
            if (isVNIS && key.includes('.')) {
                const [baseKey, provider] = key.split('.')
                displayName = `${productGroupName?.toLowerCase()}.${baseKey}`
                providerName = provider
            } else if (isVNIS) {
                displayName = `${productGroupName?.toLowerCase()}.${key}`
            }

            columns.push({
                field: columnKey,
                headerName: displayName,
                headerClassName: 'text-center',
                width: columnWidth,
                // Số liệu nằm lồng trong row.periods[kỳ][key], không phẳng theo
                // `field`. Thiếu valueGetter thì grid đọc row[field] = undefined
                // cho mọi dòng, comparator coi chúng bằng nhau và sort im lặng
                // không đổi thứ tự — mũi tên vẫn hiện nên rất khó phát hiện.
                valueGetter: (_value, row) =>
                    row.periods?.[periodKey]?.[key]?.value ?? null,
                renderHeader: () => (
                    <div className="text-center">
                        <Typogaphy className="text-xs text-gray-600 mt-1">
                            {t(displayName)}
                        </Typogaphy>
                        {providerName && (
                            <Typogaphy className="text-xs text-blue-600 font-medium">
                                {providerName.toUpperCase()}
                            </Typogaphy>
                        )}
                    </div>
                ),
                renderCell: ({ row }) => {
                    const periodData = row.periods[periodKey]
                    const usageData = periodData?.[key]

                    if (!usageData) {
                        return (
                            <Typogaphy className="text-gray-400 text-center block">
                                -
                            </Typogaphy>
                        )
                    }

                    // Get previous period data for growth comparison
                    const previousPeriodData = previousPeriodKey
                        ? row.periods[previousPeriodKey]?.[key]
                        : undefined
                    const previousValue = previousPeriodData?.value

                    return (
                        <CellUsage
                            value={usageData.value}
                            unit={usageData.unit}
                            count={usageData.count}
                            aggregationMethod={config.aggregationMethod}
                            originalValue={usageData.originalValue}
                            originalUnit={usageData.originalUnit}
                            previousValue={previousValue}
                            showGrowth={
                                !isFirstPeriod &&
                                previousValue !== undefined &&
                                displayConfig.showGrowthIndicators
                            }
                            displayConfig={displayConfig}
                        />
                    )
                },
            })
        })
    })

    return columns
}

// Legacy columns for backward compatibility (kept empty as we use generateAnalyticsColumns)
const columns: GridColDefVNW<BuiltAnalyticsData>[] = []

export default columns
